/**
 * OverviewTab — Executive summary of the Knowledge Engine
 * KPI cards + citation analytics + coverage heatmap + stale entries
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, safeFetch, authFetch, titleCase } from '../../constants';

const KE = `${API_V1}/knowledge-engine`;

// --- styles ----------------------------------------------------------------
const card = (flex = 1): React.CSSProperties => ({
  flex, background: 'var(--bg-card)', border: '1px solid var(--border-default)',
  borderRadius: 8, padding: 16, textAlign: 'center' as const,
});
const kpiValue: React.CSSProperties = {
  fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-primary)',
};
const kpiLabel: React.CSSProperties = {
  fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2,
};
const sectionTitle: React.CSSProperties = {
  fontSize: 14, fontWeight: 600, margin: '24px 0 10px', color: 'var(--text-primary)',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left' as const, padding: '8px 12px', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-subtle)',
  textTransform: 'uppercase' as const, letterSpacing: 0.5,
};
const tdStyle = (i: number): React.CSSProperties => ({
  padding: '7px 12px', fontSize: 12, color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-subtle)',
  background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
});
const tableWrap: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse' as const,
  background: 'var(--bg-card)', borderRadius: 8,
  border: '1px solid var(--border-subtle)', overflow: 'hidden',
};

function coverageColor(level: string) {
  if (level === 'full') return '#4ADE80';
  if (level === 'partial') return '#FACC15';
  return '#F87171';
}

/** Safely get entries from an object, never throws */
function safeEntries(obj: unknown): [string, unknown][] {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    try { return Object.entries(obj); } catch { return []; }
  }
  return [];
}

export default function OverviewTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ docs: 0, xbrl: 0, patterns: 0, coveragePct: 0, divergences: 0 });
  const [cited, setCited] = useState<any[]>([]);
  const [distribution, setDistribution] = useState<any[]>([]);
  const [staleEntries, setStaleEntries] = useState<any[]>([]);
  const [staleCount, setStaleCount] = useState(0);
  const [coverage, setCoverage] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);

    // Each fetch is individually wrapped — no single failure can crash the component
    const fetchDocs = safeFetch(`${API_V1}/library/documents`, { documents: [] });
    // Fetch both frameworks to get total XBRL count
    const fetchXbrlUs = safeFetch(`${KE}/xbrl/search?q=&framework=US_GAAP&limit=1`, { total: 0 });
    const fetchXbrlIfrs = safeFetch(`${KE}/xbrl/search?q=&framework=IFRS&limit=1`, { total: 0 });
    const fetchPatterns = safeFetch(`${KE}/patterns`, { count: 0 });
    const fetchCoverage = safeFetch(`${KE}/coverage-report`, {});
    const fetchDiv = safeFetch(`${KE}/xbrl/mappings/divergent`, { count: 0 });
    const fetchCited = authFetch(`${API_V1}/diagnostics/library/most-cited?limit=10`)
      .then(r => r.ok ? r.json() : []).catch(() => []);
    const fetchDist = authFetch(`${API_V1}/diagnostics/library/distribution`)
      .then(r => r.ok ? r.json() : null).catch(() => null);
    const fetchStale = authFetch(`${API_V1}/diagnostics/library/stale`)
      .then(r => r.ok ? r.json() : null).catch(() => null);

    Promise.all([
      fetchDocs, fetchXbrlUs, fetchXbrlIfrs, fetchPatterns, fetchCoverage, fetchDiv,
      fetchCited, fetchDist, fetchStale,
    ]).then((results: any[]) => {
      try {
        const [docRes, xbrlUsRes, xbrlIfrsRes, patRes, covRes, divRes, citedRes, distRes, staleRes] = results || [];

        // KPI stats
        const docs = Array.isArray(docRes?.documents) ? docRes.documents.length : 0;
        const xbrl = (Number(xbrlUsRes?.total) || Number(xbrlUsRes?.count) || 0)
                   + (Number(xbrlIfrsRes?.total) || Number(xbrlIfrsRes?.count) || 0);
        const patterns = Number(patRes?.count) || 0;
        const totalTypes = Number(covRes?.total_exception_types || covRes?.total_types) || 1;
        const withPattern = Number(covRes?.types_with_pattern || covRes?.covered) || 0;
        const coveragePct = Math.round((withPattern / totalTypes) * 100);
        const divMappings = Array.isArray(divRes?.mappings) ? divRes.mappings : [];
        const divergences = Number(divRes?.count) || divMappings.length || 0;

        setStats({ docs, xbrl, patterns, coveragePct, divergences });

        // Citations — API returns bare list or {entries: [...]}
        setCited(Array.isArray(citedRes) ? citedRes : (Array.isArray(citedRes?.entries) ? citedRes.entries : []));

        // Distribution — API returns {ranges: {...}} or fallback null/{}
        const ranges = distRes?.ranges;
        if (ranges && typeof ranges === 'object' && !Array.isArray(ranges)) {
          const entries = safeEntries(ranges);
          setDistribution(entries.map(([label, count]) => ({ range: label, count: Number(count) || 0 })));
        } else if (Array.isArray(distRes)) {
          setDistribution(distRes);
        } else if (Array.isArray(distRes?.distribution)) {
          setDistribution(distRes.distribution);
        } else {
          setDistribution([]);
        }

        // Stale — API returns bare list or {entries: [...], count: N}
        const se = Array.isArray(staleRes?.entries) ? staleRes.entries
          : Array.isArray(staleRes) ? staleRes : [];
        setStaleEntries(se);
        setStaleCount(Number(staleRes?.count) || se.length);

        // Coverage heatmap
        const gaps = Array.isArray(covRes?.gaps) ? covRes.gaps : [];
        const covered = Array.isArray(covRes?.covered_types) ? covRes.covered_types
          : Array.isArray(covRes?.types) ? covRes.types : [];
        const heatData: any[] = [];
        for (const t of covered) {
          const name = typeof t === 'string' ? t : String(t?.exception_type || t?.type || '');
          const level = typeof t === 'string' ? 'full'
            : (t?.has_xbrl && t?.has_citation ? 'full' : 'partial');
          heatData.push({ type: name, level });
        }
        for (const g of gaps) {
          const name = typeof g === 'string' ? g : String(g?.exception_type || g?.type || '');
          heatData.push({ type: name, level: 'none' });
        }
        setCoverage(heatData);
      } catch (err) {
        console.error('OverviewTab data processing error:', err);
        setError('Failed to process data. Some sections may be incomplete.');
      }
    }).catch((err) => {
      console.error('OverviewTab fetch error:', err);
      setError('Failed to load knowledge engine data.');
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ color: 'var(--text-secondary)', padding: 20 }}>Loading knowledge engine overview...</p>;

  if (error && stats.docs === 0 && stats.xbrl === 0 && stats.patterns === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: '#F87171', fontSize: 14, marginBottom: 8 }}>{error}</p>
        <button onClick={load} style={{
          padding: '8px 16px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
          background: 'var(--accent)', color: 'var(--text-primary)', border: 'none',
        }}>Retry</button>
      </div>
    );
  }

  const maxCitation = Math.max(1, ...(distribution || []).map((d: any) => Number(d?.count ?? d?.value) || 0));

  return (
    <div>
      {error && (
        <div style={{ padding: '8px 12px', marginBottom: 12, background: '#FACC1510', border: '1px solid #FACC1540', borderRadius: 6, fontSize: 12, color: '#FACC15' }}>
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={card()}>
          <div style={kpiValue}>{stats.docs}</div>
          <div style={kpiLabel}>Standards</div>
        </div>
        <div style={card()}>
          <div style={kpiValue}>{stats.xbrl}</div>
          <div style={kpiLabel}>XBRL Concepts</div>
        </div>
        <div style={card()}>
          <div style={kpiValue}>{stats.patterns}</div>
          <div style={kpiLabel}>Resolution Patterns</div>
        </div>
        <div style={card()}>
          <div style={{ ...kpiValue, color: stats.coveragePct >= 80 ? '#4ADE80' : stats.coveragePct >= 50 ? '#FACC15' : '#F87171' }}>
            {stats.coveragePct}%
          </div>
          <div style={kpiLabel}>Coverage</div>
        </div>
        <div style={card()}>
          <div style={{ ...kpiValue, color: stats.divergences > 0 ? '#FB923C' : '#4ADE80' }}>
            {stats.divergences}
          </div>
          <div style={kpiLabel}>Divergences</div>
        </div>
      </div>

      {/* Citation Analytics */}
      <div style={sectionTitle}>Most Cited References</div>
      {(cited || []).length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>No citation data available yet.</p>
      ) : (
        <table style={tableWrap}>
          <thead>
            <tr>
              <th style={thStyle}>Document</th>
              <th style={thStyle}>Section</th>
              <th style={thStyle}>Exception Type</th>
              <th style={thStyle}>Framework</th>
              <th style={thStyle}>Citations</th>
            </tr>
          </thead>
          <tbody>
            {(cited || []).slice(0, 10).map((c: any, i: number) => (
              <tr key={i}>
                <td style={{ ...tdStyle(i), fontWeight: 600 }}>{c?.document_code ?? '--'}</td>
                <td style={tdStyle(i)}>{c?.section_reference ?? '--'}</td>
                <td style={tdStyle(i)}>{titleCase(c?.exception_type) || '--'}</td>
                <td style={tdStyle(i)}>{c?.framework ?? '--'}</td>
                <td style={{ ...tdStyle(i), fontVariantNumeric: 'tabular-nums', color: 'var(--accent)' }}>
                  {c?.citation_count ?? '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Citation Distribution */}
      {(distribution || []).length > 0 && (
        <>
          <div style={sectionTitle}>Citation Distribution</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            {(distribution || []).map((d: any, i: number) => {
              const count = Number(d?.count ?? d?.value) || 0;
              const pct = (count / maxCitation) * 100;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 120, fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right', flexShrink: 0 }}>
                    {d?.range ?? d?.label ?? d?.bucket ?? `Range ${i + 1}`}
                  </div>
                  <div style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 3, height: 18, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', borderRadius: 3, minWidth: count > 0 ? 4 : 0 }} />
                  </div>
                  <div style={{ width: 40, fontSize: 11, fontVariantNumeric: 'tabular-nums', color: 'var(--text-tertiary)' }}>
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Coverage Heatmap */}
      {(coverage || []).length > 0 && (
        <>
          <div style={sectionTitle}>
            Exception Type Coverage
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 12 }}>
              <span style={{ color: '#4ADE80' }}>&#9632;</span> Fully grounded&nbsp;&nbsp;
              <span style={{ color: '#FACC15' }}>&#9632;</span> Partial&nbsp;&nbsp;
              <span style={{ color: '#F87171' }}>&#9632;</span> No pattern
            </span>
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 4, padding: 12,
            background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)',
          }}>
            {(coverage || []).map((c: any, i: number) => (
              <div key={i} title={titleCase(c?.type)} style={{
                width: 14, height: 14, borderRadius: 2,
                background: coverageColor(c?.level ?? 'none'),
                opacity: c?.level === 'none' ? 0.6 : 0.85,
                cursor: 'default',
              }} />
            ))}
          </div>
        </>
      )}

      {/* Stale Entries */}
      <div style={sectionTitle}>
        Stale Entries
        {staleCount > 0 && <span style={{ fontWeight: 400, fontSize: 12, color: '#FACC15', marginLeft: 6 }}>({staleCount})</span>}
      </div>
      {(staleEntries || []).length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>No stale entries detected.</p>
      ) : (
        <table style={tableWrap}>
          <thead>
            <tr>
              <th style={thStyle}>Document</th>
              <th style={thStyle}>Section</th>
              <th style={thStyle}>Last Used</th>
              <th style={thStyle}>Age (days)</th>
            </tr>
          </thead>
          <tbody>
            {(staleEntries || []).slice(0, 10).map((s: any, i: number) => (
              <tr key={i}>
                <td style={tdStyle(i)}>{s?.document_code ?? '--'}</td>
                <td style={tdStyle(i)}>{s?.section_reference ?? '--'}</td>
                <td style={tdStyle(i)}>{s?.last_used ? new Date(s.last_used).toLocaleDateString() : '--'}</td>
                <td style={{ ...tdStyle(i), fontVariantNumeric: 'tabular-nums' }}>{s?.age_days ?? '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
